/**
 * Developed by Ashvek Technologies · https://ashvek.com · Evidence over prompts
 */
import * as React from "react";
import { AshvekScene } from "../lib/three_scene";
import { analyticsReporter } from "../lib/analytics";

export function ThreeCanvas() {
  const canvasRef = React.useRef<HTMLCanvasElement | null>(null);
  const [webGLSupported, setWebGLSupported] = React.useState(true);
  const [contextLost, setContextLost] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!canvasRef.current) return;
    try {
      // Check WebGL 2.0 availability
      const testCanvas = document.createElement("canvas");
      const gl = testCanvas.getContext("webgl2");
      if (!gl) {
        analyticsReporter.recordEvent("WEBGL_ERROR", { reason: "WebGL 2.0 unavailable", status: "FALLBACK" });
        setWebGLSupported(false);
        return;
      }

      const scene = new AshvekScene({
        canvas: canvasRef.current,
        onContextLost: () => {
          analyticsReporter.recordEvent("WEBGL_ERROR", { status: "CONTEXT_LOST" });
          setContextLost(true);
        },
        onContextRestored: () => {
          analyticsReporter.recordEvent("WEBGL_PERFORMANCE", { status: "CONTEXT_RESTORED" });
          setContextLost(false);
        },
        onError: (err) => {
          analyticsReporter.recordEvent("WEBGL_ERROR", { error: err.message, status: "FALLBACK" });
          setErrorMessage(err.message);
          setWebGLSupported(false);
        },
      });

      analyticsReporter.recordWebGLMetrics(60, 2, 0);

      return () => scene.dispose();
    } catch (e: any) {
      analyticsReporter.recordEvent("WEBGL_ERROR", { error: e.message || "WebGL init failed", status: "FALLBACK" });
      setErrorMessage(e.message || "WebGL initialization failed");
      setWebGLSupported(false);
    }
  }, []);

  if (!webGLSupported || contextLost) {
    // Accessible, WCAG AA compliant static SVG fallback
    return (
      <div
        role="region"
        aria-label="Ashvek Technologies Precision Emblem"
        className="w-full h-80 md:h-[400px] flex flex-col items-center justify-center border border-[#1E293B] rounded-2xl bg-[#090D16]/80 p-6 space-y-4 shadow-inner"
      >
        <svg
          viewBox="0 0 100 100"
          className="w-32 h-32 stroke-[#FF3B30] fill-none stroke-[2.5]"
          role="img"
          aria-label="Ashvek Apex Precision Emblem (Static Vector Fallback)"
        >
          <polygon points="50,15 20,85 80,85" strokeLinejoin="round" />
          <line x1="32" y1="62" x2="68" y2="62" stroke="#FF9500" strokeWidth="2.5" />
        </svg>
        <div className="text-center space-y-1">
          <p className="text-xs font-mono text-[#F8FAFC]">
            {contextLost ? "GPU Context Suspended" : "Precision Vector Fallback Active"}
          </p>
          <p className="text-[11px] text-[#94A3B8]">
            {errorMessage || (contextLost ? "WebGL context was suspended by operating system." : "Running high-contrast static SVG fallback mode.")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full h-[400px] md:h-[480px] flex items-center justify-center">
      <canvas
        ref={canvasRef}
        role="img"
        aria-label="Ashvek Technologies Interactive 3D Apex Emblem (WebGL 2.0)"
        className="w-full h-full cursor-grab active:cursor-grabbing focus-visible:ring-2 focus-visible:ring-[#FF3B30] focus-visible:outline-none rounded-xl"
        tabIndex={0}
      />
      <div className="absolute bottom-4 right-4 pointer-events-none text-[10px] font-mono text-[#94A3B8]/80 uppercase tracking-widest bg-[#090D16]/60 px-2 py-1 rounded border border-[#1E293B]/40">
        WebGL 2.0 · Three.js r170 · Orbit View
      </div>
    </div>
  );
}
